import type { RequiredGeneric } from '@pba/utils';
import type { Gender } from 'models';
import type { ReactNode } from 'react';
import {
  BirthDatePicker,
  type BirthDateType,
  Col,
  Fieldset,
  type FileUploadDone,
  Form,
  ImageDragger,
  ImageDraggerWithCrop,
  PhoneInput,
  Row,
  Select,
  TextInput,
  type UploadChangeParam,
  type UploadedFileList,
  useBirthDateValidation,
  useGenerateImageUploadRulesAndValidations,
} from 'ui';
import { loadImage, timezones } from 'common/utils';
import { uploadImage } from 'features/files';

export type AboutFormValues = {
  birthdate?: BirthDateType;
  coverPhoto?: [FileUploadDone];
  email: string;
  gender?: string;
  phone: string;
  profilePhoto?: [FileUploadDone];
  timeZone: string;
};

type P = {
  children: ReactNode;
  genders: Gender[];
};

const AboutFieldset = <FormValues extends AboutFormValues = never>({
  children,
  genders,
}: RequiredGeneric<FormValues, P>) => {
  const { rules: profilePhotoRules, validations: profilePhotoValidations } =
    useGenerateImageUploadRulesAndValidations();
  const { rules: coverPhotoRules, validations: coverPhotoValidations } = useGenerateImageUploadRulesAndValidations();

  const birthdateValidatorRule = useBirthDateValidation();

  return (
    <Fieldset legend={{ title: 'About' }}>
      <Row>
        <Col span={24}>
          <Row gutter="lg">
            <Col>
              <Form.Item<AboutFormValues>
                getValueFromEvent={({ fileList }: UploadChangeParam): UploadedFileList => fileList}
                label="Avatar"
                name="profilePhoto"
                rules={profilePhotoRules}
                valuePropName="fileList"
              >
                <ImageDraggerWithCrop
                  cropper={{ aspect: '1:1', cropShape: 'round' }}
                  dragger={{
                    alt: 'User profile photo',
                    loadImage,
                    shape: 'circle',
                    size: 'medium',
                    uploadFile: uploadImage,
                    validations: profilePhotoValidations,
                  }}
                />
              </Form.Item>
            </Col>
            <Col flex="1 1 0">
              <Form.Item<AboutFormValues>
                getValueFromEvent={({ fileList }: UploadChangeParam): UploadedFileList => fileList}
                label="Background image"
                name="coverPhoto"
                rules={coverPhotoRules}
                valuePropName="fileList"
              >
                <ImageDragger
                  alt="User cover photo"
                  loadImage={loadImage}
                  shape="rectangle-stretch"
                  size="medium"
                  uploadFile={uploadImage}
                  validations={coverPhotoValidations}
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>
        <Col span={24}>{children}</Col>
        <Col span={24}>
          <TextInput
            input={{
              autoComplete: 'email',
            }}
            item={{
              label: 'E-mail',
              name: 'email',
              rules: [
                { type: 'email' },
                {
                  required: true,
                },
              ],
            }}
          />
        </Col>
        <Col span={24}>
          <PhoneInput
            input={{
              autoComplete: 'tel',
            }}
            item={{
              label: 'Phone number',
              name: 'phone',
              rules: [
                {
                  required: true,
                },
              ],
            }}
          />
        </Col>
        <Col span={24}>
          <Form.Item<AboutFormValues>
            label="Date of birth"
            name="birthdate"
            rules={[birthdateValidatorRule({ isRequired: true }), { required: true }]}
          >
            <BirthDatePicker />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item<AboutFormValues> label="My timezone" name="timeZone" rules={[{ required: true }]}>
            <Select
              optionFilterProp="label"
              options={timezones.map((timezone) => ({
                label: timezone.label,
                value: timezone.value,
              }))}
              showSearch
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item<AboutFormValues> label="Gender" name="gender">
            <Select
              optionFilterProp="label"
              options={genders.map((gender) => ({
                label: gender.name,
                value: gender.id,
              }))}
              showSearch
            />
          </Form.Item>
        </Col>
      </Row>
    </Fieldset>
  );
};

export default AboutFieldset;
